import { Helmet } from 'react-helmet';
import { PageHeader } from 'shared-components';

import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import * as types from 'types';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';
import { LOCKED_SECTION_SUMMARY } from '../../../../utilities/constants';
import PageSubtext from '../../../atoms/PageSubtext';
import { titleAppend } from '../../../../utilities/head';
import {
  canShowAgentQuestion,
  canShowVisaSupportQuestion,
  canShowWhatsappQuestion,
  canShowOfferEvidenceQuestion
} from '../../../../utilities/pages';
import { createNavigateToAction } from '../../../../middleware/actions';

export interface IDispatchProps {
  onResume: (url: string) => void;
}

interface FingerStatus {
  finger: string;
  title: string;
  complete: boolean;
}
export interface IProps {
  title: string;
  summary: string;
  personalInfoTitle: string;
  yourHistoryTitle: string;
  consentsTitle: string;
  financialTitle: string;
  statusArray: Array<FingerStatus>;
  historySectionStatus: types.SectionStatusType;
  financeSectionStatus: types.SectionStatusType;
}

const useData = () => {
  const student = useSelector((state: ApplicationState) => state.student);
  const config = useSelector((state: ApplicationState) => state.config);
  const {
    isPIPage1Complete: PIP1 = false,
    isPIPage2Complete: PIP2 = false,
    isPIPage3Complete: PIP3 = false,
    isPIPage4Complete: PIP4 = false,
    isPIPage5Complete: PIP5 = false,
    isPIPage5_1Complete: PIP5_1 = false,
    isPIPage6Complete: PIP6 = false,
    isPIPage7Complete: PIP7 = false,
    isPIPage8Complete: PIP8 = false,
    isPIPage9Complete: PIP9 = false,
    isPIPage10Complete: PIP10 = false
  } = student.offer;

  const { isHPage1Complete: HP1 = false, isHPage2Complete: HP2 = false, isHPage3Complete: HP3 = false } = student.offer;

  const {
    isPage1Complete: CP1 = false,
    isPage2Complete: CP2 = false,
    isPage3Complete: CP3 = false,
    isPage4Complete: CP4 = false
  } = student.consents;

  const FP1 = !!student.financials.isSectionComplete;

  const {
    Page1: PIPage1,
    Page2: PIPage2,
    Page3: PIPage3,
    Page4: PIPage4,
    Page5: PIPage5,
    Page5_1: PIPage5_1,
    Page6: PIPage6,
    Page7: PIPage7,
    Page8: PIPage8,
    Page9: PIPage9,
    Page10: PIPage10
  } = config.Checklist.PersonalInfo;

  const { Page1: HPage1, Page2: HPage2, Page3: HPage3 } = config.Checklist.History;
  const historySectionStatus = config.historySectionStatus;

  const { Page1: CPage1, Page2: CPage2, Page3: CPage3, Page4: CPage4 } = config.Checklist.Consents;

  const { Page1: FPage1 } = config.Checklist.Finances;
  const financeSectionStatus = config.financeSectionStatus;

  const showAgentQuestion = canShowAgentQuestion(student.importData, student.offer);

  const showVisaSupportQuestion = canShowVisaSupportQuestion(
    student.personalInfo,
    student.importData,
    config,
    student.offer
  );
  const showWhatsappQuestion = canShowWhatsappQuestion(student.offer);
  const showOfferEvidenceQuestion = canShowOfferEvidenceQuestion(
    student.offer,
    student.importData,
    student.conflictHistory,
    config
  );

  // prettier-ignore
  const statusArray = [
    ...(!PIPage1.hidden ? [{ finger: 'PIP1', title: PIPage1.title, complete: PIP1 }] : []),
    ...(!PIPage2.hidden ? [{ finger: 'PIP2', title: PIPage2.title, complete: PIP2 }] : []),
    ...(!PIPage3.hidden ? [{ finger: 'PIP3', title: PIPage3.title, complete: PIP3 }] : []),
    ...(!PIPage4.hidden ? [{ finger: 'PIP4', title: PIPage4.title, complete: PIP4 }] : []),
    ...(!PIPage5.hidden ? [{ finger: 'PIP5', title: PIPage5.title, complete: PIP5 }] : []),
    ...(!PIPage5_1.hidden && showOfferEvidenceQuestion ? [{ finger: 'PIP5_1', title: PIPage5_1.title, complete: PIP5_1 }] : []),
    ...(!PIPage6.hidden ? [{ finger: 'PIP6', title: PIPage6.title, complete: PIP6 }] : []),
    ...(!PIPage7.hidden ? [{ finger: 'PIP7', title: PIPage7.title, complete: PIP7 }] : []),
    ...(!PIPage8.hidden && showAgentQuestion ? [{ finger: 'PIP8', title: PIPage8.title, complete: PIP8 }] : []),
    ...(
      !PIPage9.hidden && showVisaSupportQuestion
        ? [{ finger: 'PIP9', title: PIPage9.title, complete: PIP9 }]
        : []),
    ...((!PIPage10.hidden && showWhatsappQuestion)
      ? [{ finger: 'PIP10', title: PIPage10.title, complete: PIP10 }]
      : []),
    { finger: 'HP1', title: HPage1.title, complete: HP1 },
    { finger: 'HP2', title: HPage2.title, complete: HP2 },
    { finger: 'HP3', title: HPage3.title, complete: HP3 },
    ...(!CPage1.hidden ? [{ finger: 'CP1', title: CPage1.title, complete: CP1 }] : []),
    ...(!CPage2.hidden ? [{ finger: 'CP2', title: CPage2.title, complete: CP2 }] : []),
    ...((!CPage3.hidden && student.offer.visaSupport) ? [{ finger: 'CP3', title: CPage3.title, complete: CP3 }] : []),
    ...(!CPage4.hidden ? [{ finger: 'CP4', title: CPage4.title, complete: CP4 }] : []),
    { finger: 'FP1', title: FPage1.title, complete: FP1 }
  ];

  return {
    title: config.sections.CASChecklist.summaryLabel,
    summary: config.sections.CASChecklist.summary,
    personalInfoTitle: config.Checklist.PersonalInfo.shortTitle,
    yourHistoryTitle: config.Checklist.History.shortTitle,
    consentsTitle: config.Checklist.Consents.shortTitle,
    financialTitle: config.Checklist.Finances.shortTitle,
    statusArray,
    historySectionStatus,
    financeSectionStatus
  };
};

const Checklist = () => {
  const dispatch = useDispatch();
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const props = useData();
  const isSectionLocked = useIsSectionLocked(SectionId.CHECKLIST, offer.status);

  const onResume = (url: string) => {
    dispatch(createNavigateToAction(url));
  };

  const handleActionClick = (target: string) => {
    switch (target) {
      case 'PIP1':
        onResume('/personal-info/step-1');
        break;
      case 'PIP2':
        onResume('/personal-info/step-2');
        break;
      case 'PIP3':
        onResume('/personal-info/step-3');
        break;
      case 'PIP4':
        onResume('/personal-info/step-4');
        break;
      case 'PIP5':
        onResume('/personal-info/step-5');
        break;
      case 'PIP5_1':
        onResume('/personal-info/step-5-1');
        break;
      case 'PIP6':
        onResume('/personal-info/step-6');
        break;
      case 'PIP7':
        onResume('/personal-info/step-7');
        break;
      case 'PIP8':
        onResume('/personal-info/step-8');
        break;
      case 'PIP9':
        onResume('/personal-info/step-9');
        break;
      case 'PIP10':
        onResume('/personal-info/step-10');
        break;
      case 'HP1':
        onResume('/history/step-1');
        break;
      case 'HP2':
        onResume('/history/step-2');
        break;
      case 'HP3':
        onResume('/history/step-3');
        break;
      case 'HP4':
        onResume('/history/step-4');
        break;
      case 'CP1':
        onResume('/consents/step-1');
        break;
      case 'CP2':
        onResume('/consents/step-2');
        break;
      case 'CP3':
        onResume('/consents/step-3');
        break;
      case 'CP4':
        onResume('/consents/step-4');
        break;
      case 'FP1':
        onResume('/finances/step-1');
        break;
    }
  };

  const getFingerStatus = (finger: FingerStatus) => {
    if (isSectionLocked) return Status.Locked;
    if (finger.complete) return Status.Complete;
    const isFingerInStatusArray = props.statusArray.indexOf(finger) > 0;
    if (isFingerInStatusArray) {
      // check a previous finger status
      const isPreviousFingerComplete = props.statusArray[props.statusArray.indexOf(finger) - 1].complete;
      if (isPreviousFingerComplete) {
        return Status.InProgress;
      } else {
        return Status.Todo;
      }
    }
    return Status.InProgress;
  };

  return (
    <>
      <Helmet>
        <title>
          {props.title} {titleAppend}
        </title>
      </Helmet>

      <PageHeader dataSelector="Dashboard-Header">{props.title}</PageHeader>

      <PageSubtext>
        <div dangerouslySetInnerHTML={{ __html: isSectionLocked ? LOCKED_SECTION_SUMMARY : props.summary }} />
      </PageSubtext>

      <ChecklistSection label={props.personalInfoTitle} id={'personalInfo'}>
        {props.statusArray
          .filter(page => page.finger.startsWith('PIP'))
          .map(finger => (
            <Finger
              id={finger.finger}
              key={finger.finger}
              status={getFingerStatus(finger)}
              label={finger.title!}
              onClick={() => handleActionClick(finger.finger)}
            />
          ))}
      </ChecklistSection>

      {props.historySectionStatus === types.SectionStatusType.ACTIVE && (
        <ChecklistSection label={props.yourHistoryTitle} id={'history'}>
          {props.statusArray
            .filter(page => page.finger.startsWith('HP'))
            .map(finger => (
              <Finger
                id={finger.finger}
                key={finger.finger}
                status={getFingerStatus(finger)}
                label={finger.title}
                onClick={() => handleActionClick(finger.finger)}
              />
            ))}
        </ChecklistSection>
      )}

      <ChecklistSection label={props.consentsTitle} id={'consents'}>
        {props.statusArray
          .filter(page => page.finger.startsWith('CP'))
          .map(finger => (
            <Finger
              id={finger.finger}
              key={finger.finger}
              status={getFingerStatus(finger)}
              label={finger.title}
              onClick={() => handleActionClick(finger.finger)}
            />
          ))}
      </ChecklistSection>

      {props.financeSectionStatus === types.SectionStatusType.ACTIVE && (
        <ChecklistSection label={props.financialTitle} id={'financials'}>
          {props.statusArray
            .filter(page => page.finger.startsWith('FP'))
            .map(finger => (
              <Finger
                id={finger.finger}
                key={finger.finger}
                status={getFingerStatus(finger)}
                label={finger.title}
                onClick={() => handleActionClick(finger.finger)}
              />
            ))}
        </ChecklistSection>
      )}
    </>
  );
};

export default Checklist;
